/**
 * Canonical single-choice option lists for "Bulunduğu Kat" and "Bina Yaşı".
 *
 * Storage contract (must match backend Pydantic validators):
 *   - Floor: descriptive strings ("Bahçe katı") and numeric-as-string ("3").
 *   - Building Age: integer 0..50 or the string "Yapım aşamasında".
 *
 * Mapping helpers convert a stored raw value to a select option value.
 * If the raw value does not map to any option, the caller should render
 * a "Mevcut kayıt: …" legacy hint and keep the select unselected. The
 * original value is preserved verbatim on save.
 */

// Special sentinel used ONLY inside the shadcn <Select> — it cannot accept an
// empty string as an item value. Callers strip this back to "" before saving.
export const UNSELECTED_SENTINEL = "__unselected__";

const FLOOR_DESCRIPTIVE_HEAD = [
  "Bodrum kat",
  "Bahçe katı",
  "Zemin kat",
  "Yüksek giriş",
  "Ara kat",
];

const FLOOR_DESCRIPTIVE_TAIL = [
  "Çatı katı",
  "Çatı dubleksi",
  "Dubleks",
  "Bilinmiyor",
];

/**
 * FLOOR_OPTIONS: [{ value, label }]
 *   - numeric labels "1. kat" store as "1", "2. kat" → "2", ...
 *   - descriptive labels store their own text.
 */
export const FLOOR_OPTIONS = [
  ...FLOOR_DESCRIPTIVE_HEAD.map((s) => ({ value: s, label: s })),
  ...Array.from({ length: 50 }, (_, i) => {
    const n = i + 1;
    return { value: String(n), label: `${n}. kat` };
  }),
  ...FLOOR_DESCRIPTIVE_TAIL.map((s) => ({ value: s, label: s })),
];

/**
 * BUILDING_AGE_OPTIONS: [{ value, label }]
 *   - "Yapım aşamasında" (string, non-numeric)
 *   - "0" → "0 — Sıfır bina"
 *   - "1".."50" → same numeric label
 */
export const BUILDING_AGE_OPTIONS = [
  { value: "Yapım aşamasında", label: "Yapım aşamasında" },
  { value: "0", label: "0 — Sıfır bina" },
  ...Array.from({ length: 50 }, (_, i) => {
    const n = i + 1;
    return { value: String(n), label: String(n) };
  }),
];

const FLOOR_VALUES = new Set(FLOOR_OPTIONS.map((o) => o.value));
const BUILDING_AGE_VALUES = new Set(BUILDING_AGE_OPTIONS.map((o) => o.value));

/**
 * Map a stored floor value to its option value, or return "" if the value
 * cannot be represented in the fixed option list.
 */
export function mapStoredFloorToOption(stored) {
  if (stored == null) return "";
  const s = typeof stored === "number" ? String(stored) : String(stored).trim();
  if (s === "") return "";
  if (FLOOR_VALUES.has(s)) return s;
  return "";
}

/**
 * Map a stored building_age value to its option value, or return "" if it
 * cannot be represented in the fixed option list.
 */
export function mapStoredAgeToOption(stored) {
  if (stored == null) return "";
  // Booleans should never be stored, but be defensive.
  if (typeof stored === "boolean") return "";
  const s =
    typeof stored === "number"
      ? String(Math.trunc(stored))
      : String(stored).trim();
  if (s === "") return "";
  if (BUILDING_AGE_VALUES.has(s)) return s;
  return "";
}

/**
 * Coerce a selected building-age option value to the correct payload type
 * before sending to the API. "0".."50" become integers; "Yapım aşamasında"
 * stays as a string; everything else returns null.
 */
export function ageOptionToPayload(value) {
  if (value == null || value === "" || value === UNSELECTED_SENTINEL) return null;
  if (value === "Yapım aşamasında") return "Yapım aşamasında";
  const n = Number(value);
  if (Number.isInteger(n) && n >= 0 && n <= 50) return n;
  return null;
}

/**
 * Floor payload: options store the string value directly (numbers stay as
 * strings like "3" per existing schema). Empty/sentinel → null.
 */
export function floorOptionToPayload(value) {
  if (value == null || value === UNSELECTED_SENTINEL) return null;
  const s = String(value).trim();
  return s === "" ? null : s;
}
